import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { OfflineTimeInsight } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { InsightResponseWithPreviousInterval } from 'modules/insights-v2/types';
import {
  formatGrowthRate,
  formatMinutes,
  formatPercentage,
  getGrowthRateForFields,
  getGrowthTrend,
} from 'modules/insights-v2/utils';

import MetricBlock from '../../OverallPerformance/MetricBlock';

type OfflineTimeSummaryProps = {
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  children: React.ReactNode;
};

const OfflineTimeSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;

  padding-block: 16px;
  padding-inline: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 0;
  }
`;

const MetricsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-block: 16px;
    padding-inline: 24px;
  }
`;

const LegendsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  justify-content: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    padding: 24px;
    align-items: flex-start;
  }
`;

const DEFAULT_OFFLINE_TOTAL = {
  openingMinutes: 0,
  offlineMinutes: 0,
  unavailableMinutes: 0,
  unavailablePercentage: 0,
  nonAliveMinutes: 0,
  nonAliveTimePercentage: 0,
  offlineTimePercentage: 0,
};

const OfflineTimeSummary = ({ offlineTimeInsight, children }: OfflineTimeSummaryProps) => {
  const { formatMessage, locale } = useIntl();

  const offlineTimeTotalForSelected =
    offlineTimeInsight.selected.data?.data.offlineTime?.total ?? DEFAULT_OFFLINE_TOTAL;
  const offlineTimeTotalForPrevious =
    offlineTimeInsight.previous.data?.data.offlineTime?.total ?? DEFAULT_OFFLINE_TOTAL;

  const growthRateForFields = getGrowthRateForFields(offlineTimeTotalForSelected, offlineTimeTotalForPrevious, [
    'unavailableMinutes',
    'unavailablePercentage',
  ]);

  return (
    <OfflineTimeSummaryContainer>
      <MetricsContainer>
        <MetricBlock
          title={formatMessage({ id: 'insights.offline-time.unavailable-hours' })}
          tooltip={formatMessage({ id: 'insights.offline-time.unavailable-hours-tooltip' })}
          metrics={formatMinutes(offlineTimeTotalForSelected.unavailableMinutes)}
          growth={formatGrowthRate(growthRateForFields.unavailableMinutes, locale)}
          growthTrend={getGrowthTrend(growthRateForFields.unavailableMinutes, true)}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.offline-time.percent-unavailable' })}
          tooltip={formatMessage({ id: 'insights.offline-time.percent-unavailable-tooltip' })}
          metrics={
            offlineTimeTotalForSelected.unavailablePercentage >= 1 / 100
              ? formatPercentage(offlineTimeTotalForSelected.unavailablePercentage, locale)
              : `<${formatPercentage(1 / 100, locale)}`
          }
          growth={formatGrowthRate(growthRateForFields.unavailablePercentage, locale)}
          growthTrend={getGrowthTrend(growthRateForFields.unavailablePercentage, true)}
        />
      </MetricsContainer>
      <LegendsWrapper>{children}</LegendsWrapper>
    </OfflineTimeSummaryContainer>
  );
};
export default OfflineTimeSummary;
